import React, { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { LayoutDashboard, Users, CalendarDays, Bell, LogOut, X, Camera, Menu } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";

const NAV = [
  { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard, end: true },
  { to: "/admin/clientes", label: "Mes clientes", icon: Users },
  { to: "/admin/calendrier", label: "Mon calendrier", icon: CalendarDays },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [photo, setPhoto] = useState("/fanny.png");
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/profile").then(({ data }) => { if (data.photo) setPhoto(data.photo); }).catch(() => {});
  }, []);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(data.photo);
    } catch (err) { /* ignore */ }
  };

  useEffect(() => {
    api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
  }, []);

  const unread = notifs.filter((n) => !n.read).length;
  const dismiss = async (nid) => { await api.delete(`/notifications/${nid}`); setNotifs((p) => p.filter((n) => n.id !== nid)); };
  const clearAll = async () => { await api.delete("/notifications"); setNotifs([]); };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarInner = (
    <>
        <div className="relative flex h-[72px] items-center px-8">
          <span className="logo-elevate text-4xl" style={{ color: "#FFFCF6" }}>elevate</span>
          <span className="absolute inset-x-8 bottom-0 border-t border-cream/25" />
        </div>
        <div className="mx-8 mb-2 mt-5 rounded-xl bg-white/5 px-4 py-3">
          <p className="text-[0.62rem] uppercase tracking-[0.28em] text-cream/50">Espace</p>
          <p className="mt-0.5 font-sans text-base font-medium text-cream">Admin</p>
        </div>
        <nav className="mt-3 flex-1 space-y-1.5 px-5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobile(false)}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition ${
                  isActive ? "bg-white/15 text-white" : "text-cream/70 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-5">
          <button
            data-testid="admin-logout-button"
            onClick={doLogout}
            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm text-cream/70 transition hover:bg-white/10 hover:text-white"
          >
            <LogOut size={18} /> Se déconnecter
          </button>
        </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-cream">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-72 flex-col border-r border-white/10 bg-gradient-to-b from-bordeaux to-bordeaux-dark lg:flex">
        {SidebarInner}
      </aside>

      {mobile && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0" onClick={() => setMobile(false)} />
          <aside data-testid="admin-mobile-menu" className="absolute inset-y-0 left-0 flex w-1/2 min-w-[250px] animate-[slideIn_.25s_ease-out] flex-col overflow-y-auto bg-gradient-to-b from-bordeaux to-bordeaux-dark shadow-[12px_0_40px_rgba(45,24,30,0.25)]">
            <button data-testid="admin-mobile-menu-close" onClick={() => setMobile(false)} className="absolute right-4 top-4 z-10 text-cream/70"><X size={20} /></button>
            {SidebarInner}
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="min-w-0 flex-1 lg:pl-72">
        <header className="sticky top-0 z-20 flex h-[72px] items-center justify-between border-b border-line bg-cream/80 px-6 backdrop-blur-md lg:px-10">
          <button data-testid="admin-mobile-menu-button" className="lg:hidden text-bordeaux" onClick={() => setMobile(true)}><Menu size={24} /></button>
          <div className="ml-auto flex items-center gap-4">
            <div className="relative">
              <button
                data-testid="admin-notifications-button"
                onClick={() => setOpen((o) => !o)}
                className="relative rounded-full border border-line bg-white p-2.5 text-ink-soft transition hover:text-bordeaux"
              >
                <Bell size={18} />
                {unread > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-bordeaux text-[10px] text-white">
                    {unread}
                  </span>
                )}
              </button>
              {open && (
                <div className="absolute right-0 mt-2 w-80 rounded-2xl border border-line bg-white p-3 shadow-card">
                  <div className="flex items-center justify-between px-2 py-1">
                    <p className="text-sm font-semibold text-ink">Notifications</p>
                    {notifs.length > 0 && <button data-testid="admin-notif-clear-all" onClick={clearAll} className="text-xs text-bordeaux hover:underline">Tout effacer</button>}
                  </div>
                  {notifs.length === 0 && <p className="px-2 py-3 text-sm text-ink-muted">Rien de neuf.</p>}
                  {notifs.slice(0, 8).map((n) => (
                    <div key={n.id} className="group flex items-start gap-2 rounded-lg px-2 py-2 text-sm text-ink-soft hover:bg-bordeaux-tint">
                      <span className="flex-1">{n.message}</span>
                      <button data-testid={`admin-notif-dismiss-${n.id}`} onClick={() => dismiss(n.id)} className="mt-0.5 shrink-0 text-ink-muted transition hover:text-bordeaux"><X size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                data-testid="admin-avatar-button"
                onClick={() => fileRef.current?.click()}
                title="Changer ma photo"
                className="group relative h-9 w-9 overflow-hidden rounded-full ring-2 ring-bordeaux-border"
              >
                <img src={photo} alt="Fanny" className="h-full w-full object-cover" />
                <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition group-hover:opacity-100">
                  <Camera size={14} className="text-white" />
                </span>
              </button>
              <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="admin-avatar-input" />
              <span className="hidden text-sm font-medium text-ink sm:block">Fanny</span>
            </div>
          </div>
        </header>
        <main className="px-6 py-8 lg:px-14 lg:py-12">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
